import {
  parseArray,
  parseBoolean,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson, RolodexEntryType } from './rolodexEntryFields'

export type VisualInspectionFieldType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'select'
  | 'multi-select'
  | 'date'

export interface VisualInspectionValidationRules {
  min_length?: number
  max_length?: number
  min_value?: number
  max_value?: number
  pattern?: string
}

/**
 * Type guard to check if a string is a valid VisualInspectionFieldType
 * @param value The value to check
 * @returns True if the value is a valid VisualInspectionFieldType
 */
export function isVisualInspectionFieldType(
  value: unknown
): value is VisualInspectionFieldType {
  return (
    typeof value === 'string' &&
    ['text', 'number', 'boolean', 'select', 'multi-select', 'date'].includes(
      value
    )
  )
}

/**
 * An entry which represents a field within a visual inspection form section.
 * Fields define the individual data points to be collected during inspection.
 */
export class GeckoVisualInspectionField extends GeckoEntryBase<
  {
    label: string
    description: string
    field_type: VisualInspectionFieldType
    required: boolean
    photographable: boolean
    options?: string[]
    order: number
    validation_rules?: VisualInspectionValidationRules
  },
  {
    organization: string
    site: string
    [RolodexEntryType.VISUAL_INSPECTION_FORM]: string
    [RolodexEntryType.VISUAL_INSPECTION_SECTION]: string
  },
  Record<string, never>
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const safeFieldType = isVisualInspectionFieldType(json.data.field_type)
      ? json.data.field_type
      : 'text'

    if (
      json.data.field_type &&
      !isVisualInspectionFieldType(json.data.field_type)
    ) {
      console.warn(
        `Unknown field_type "${json.data.field_type}", defaulting to 'text'`
      )
    }

    const options = parseArray<string>(json.data.options)

    const validationRules = json.data.validation_rules as
      | Record<string, unknown>
      | undefined
    const parsedValidationRules: VisualInspectionValidationRules | undefined =
      validationRules
        ? {
            min_length: parseNumber(validationRules.min_length),
            max_length: parseNumber(validationRules.max_length),
            min_value: parseNumber(validationRules.min_value),
            max_value: parseNumber(validationRules.max_value),
            pattern: parseString(validationRules.pattern),
          }
        : undefined

    super(
      json,
      {
        label: parseString(json.data.label) ?? '',
        description: parseString(json.data.description) ?? '',
        field_type: safeFieldType,
        required: parseBoolean(json.data.required) ?? false,
        photographable: parseBoolean(json.data.photographable) ?? false,
        options,
        order: parseNumber(json.data.order) ?? 0,
        validation_rules: parsedValidationRules,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION_FORM]:
          parseString(json.tags[RolodexEntryType.VISUAL_INSPECTION_FORM]) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION_SECTION]:
          parseString(json.tags[RolodexEntryType.VISUAL_INSPECTION_SECTION]) ??
          '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a visual inspection field from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoVisualInspectionField(json)
  }
}
